// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@stoneage/MobiusOverrides/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React from "react";
import { css } from "styled-components";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";

interface Props {
    products: ProductAutocompleteItemModel[];
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
    // XNG-Changes
    doSearch: () => void;
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
    // XNG-Changes
    headerWrapper?: InjectableCss;
    headerLink?: LinkPresentationProps;
    productsWrapper?: InjectableCss;
    erpNumberValue?: TypographyPresentationProps;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        weight: "bold",
        css: css`
            text-align: left;
            margin: 10px 0 5px 0;
        `,
    },
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
        `,
    },
    focusedContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
            background-color: lightgray;
        `,
    },
    imageGridItem: {
        width: 3,
        css: css`
            max-width: 65px;
            max-height: 50px;
            flex-basis: 65px;
        `,
    },
    image: {
        width: "50px",
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    infoGridItem: {
        width: 9,
        css: css`
            flex-direction: column;
            max-width: calc(100% - 65px);
            flex-basis: calc(100% - 65px);
        `,
    },
    titleLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
            `,
        },
        css: css`
            width: 270px;
        `,
    },
    erpNumberText: {
        ellipsis: true,
        css: css`
            width: 270px;
            text-align: left;
            margin: 5px 0 0 0;
        `,
    },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products } = this.props;
        const styles = this.styles;
        return (
            <StyledWrapper data-test-selector="AutocompleteProducts">
                <StyledWrapper {...styles.headerWrapper}>
                    <Typography
                        {...styles.headerText}
                        data-test-selector="autocompleteProducts_Header"
                        className="autocomplete-products-header"
                    >
                        {translate("Products")} ({products.length})
                    </Typography>

                    {products.length > 1 && (
                        <Link
                            onClick={() => {
                                this.props.doSearch();
                            }}
                            {...styles.headerLink}
                        >
                            {translate("View All")}
                        </Link>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.productsWrapper}>
                    {products.map(product => (
                        <GridContainer
                            {...styles.container}
                            key={product.id!}
                            onClick={() => {
                                this.props.goToUrl(product.url);
                            }}
                            className="autocomplete-products-container"
                        >
                            <GridItem {...styles.imageGridItem} className="autocomplete-products-image-grid-item">
                                <LazyImage
                                    {...styles.image}
                                    src={product.image}
                                    width="64"
                                    height="64"
                                    className="autocomplete-products-image"
                                    imageKey="category"
                                    altText={product.erpNumber}
                                />
                            </GridItem>
                            <GridItem {...styles.infoGridItem} className="autocomplete-products-info-grid-item">
                                <Link
                                    {...styles.titleLink}
                                    data-test-selector={`productId_${product.id}`}
                                    className="autocomplete-products-link"
                                >
                                    {product.displayTitle}
                                </Link>
                                <Typography
                                    {...styles.erpNumberText}
                                    data-test-selector="autocompleteProducts_PartNumber"
                                    className="autocomplete-products-erp-number"
                                >
                                    {product.isNameCustomerOverride ? (
                                        <>
                                            {translate("My Part #")}:&nbsp;
                                            <Typography>{product.displayName}</Typography>
                                        </>
                                    ) : (
                                        <>
                                            {translate("Part #")}:&nbsp;
                                            <Typography {...styles.erpNumberValue}>
                                                {product.displayErpNumber}
                                            </Typography>
                                        </>
                                    )}
                                    {/* {product.manufacturerItemNumber && (
                                    <>
                                        &nbsp;
                                        {translate("MFG #")}
                                        {product.displayManufacturerItemNumber}
                                    </>
                                )} */}
                                </Typography>
                            </GridItem>
                        </GridContainer>
                    ))}
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

export default AutocompleteProducts;
